<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiSwitch from 'dashboard/components-next/switch/Switch.vue';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
    AiSwitch,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      aiEnabled: false, // Estado da IA
      isInitialized: false, // Flag para controlar o watcher
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getAllTeams',
      sectors: 'sectors/getAllSectors',
      getTeamMembers: 'teamMembers/getTeamMembers',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      const filtered = this.assignedSector
        ? this.teams.filter(t => t.sector_id === this.assignedSector.id)
        : this.teams;
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...filtered,
        ];
      }
      return filtered;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', null);
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
        if (team?.id) {
          this.$store.dispatch('teamMembers/get', { teamId: team.id });
        }
      },
    },
    sectorsList() {
      if (this.currentChat?.meta?.sector) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.sectors,
        ];
      }
      return this.sectors;
    },
    assignedSector: {
      get() {
        return this.currentChat.meta.sector;
      },
      set(sector) {
        const conversationId = this.currentChat.id;
        const sectorId = sector ? sector.id : 0;
        this.$store.dispatch('setCurrentChatTeam', {
          team: null,
          conversationId,
        });
        this.$store.dispatch('setCurrentChatAssignee', null);
        this.$store.dispatch('setCurrentChatSector', {
          sector,
          conversationId,
        });
        this.$store.dispatch('assignSector', { conversationId, sectorId });
      },
    },
    filteredAgentsList() {
      const noneOption = {
        id: 0,
        name: this.$t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE'),
      };
      let agents;
      if (!this.assignedTeam) {
        agents = this.agentsList.filter(a => a.id !== 0);
      } else {
        const members = this.getTeamMembers(this.assignedTeam.id);
        agents = members.length
          ? members.filter(a => a.id !== 0)
          : this.agentsList.filter(a => a.id !== 0);
      }
      return this.assignedAgent ? [noneOption, ...agents] : agents;
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    accountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  watch: {
    currentChat: {
      handler(newChat) {
        if (newChat) {
          const aiEnabledValue = newChat.custom_attributes?.ai_enabled;
          this.aiEnabled = aiEnabledValue === true;
          this.isInitialized = true;
          if (newChat.meta?.team?.id) {
            this.$store.dispatch('teamMembers/get', {
              teamId: newChat.meta.team.id,
            });
          }
          this.$store.dispatch('sectors/getAll');
          if (newChat.meta?.sector?.id) {
            this.$store.dispatch(
              'teams/getAllBySector',
              newChat.meta.sector.id
            );
          } else {
            this.$store.dispatch('teams/getAllBySector', null);
          }
        }
      },
      immediate: true,
    },
    aiEnabled(newValue, oldValue) {
      // Evita chamada na inicialização
      if (
        this.isInitialized &&
        oldValue !== undefined &&
        newValue !== oldValue
      ) {
        this.updateAiStatus(newValue);
      }
    },
  },
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (
        selectedItem.id === 0 ||
        (this.assignedAgent && this.assignedAgent.id === selectedItem.id)
      ) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignSector(selectedSector) {
      if (this.assignedSector && this.assignedSector.id === selectedSector.id) {
        this.assignedSector = null;
        this.$store.dispatch('teams/getAllBySector', null);
      } else {
        this.assignedSector = selectedSector;
        this.$store
          .dispatch('teams/getAllBySector', selectedSector.id)
          .then(() => {
            const defaultTeamName = `${selectedSector.name} - geral`;
            const defaultTeam = this.teams.find(
              t =>
                t.sector_id === selectedSector.id &&
                t.name.toLowerCase() === defaultTeamName.toLowerCase()
            );
            if (defaultTeam) {
              this.assignedTeam = defaultTeam;
            }
          });
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },

    async updateAiStatus(newValue) {
      try {
        await fetch(
          `/api/v1/accounts/${this.accountId}/conversations/${this.conversationId}/toggle_ai_status`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute('content'),
            },
            body: JSON.stringify({
              ai_enabled: newValue,
            }),
          }
        );
        this.$store.dispatch('updateCustomAttributes', {
          conversationId: this.conversationId,
          customAttributes: {
            ...this.currentChat.custom_attributes,
            ai_enabled: newValue,
          },
        });
      } catch (error) {
        this.aiEnabled = !newValue;
      }
    },
  },
};
</script>

<template>
  <div class="bg-white dark:bg-n-background">
    <!-- Situação do agente de IA -->
    <span class="text-sm font-medium text-black dark:text-n-slate-12">
      {{ $t('CONVERSATION.AI_STATUS.TITLE') }}
    </span>
    <div
      class="my-2 p-3 rounded-lg relative"
      :class="
        aiEnabled
          ? 'ai-gradient-border bg-white dark:bg-black'
          : 'border border-n-weak bg-white dark:bg-n-background'
      "
    >
      <div
        class="flex items-center justify-between mb-2 text-xs text-black dark:text-white"
      >
        <span v-if="aiEnabled">
          {{ $t('CONVERSATION.AI_STATUS.ACTIVE') }}
        </span>
        <span v-else>
          {{ $t('CONVERSATION.AI_STATUS.INACTIVE') }}
        </span>
        <AiSwitch v-model="aiEnabled" />
      </div>
      <div class="text-xs text-black dark:text-white" />
      <div
        class="text-[12px] text-[#636363] dark:text-[#B4B4B4] mt-1 w-[245px]"
      >
        <span v-if="aiEnabled">{{
          $t('CONVERSATION.AI_STATUS.ACTIVE_DESCRIPTION')
        }}</span>
        <span v-else>{{
          $t('CONVERSATION.AI_STATUS.INACTIVE_DESCRIPTION')
        }}</span>
      </div>
    </div>

    <div v-if="sectorsList.length" class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.SECTOR_LABEL')"
      />
      <MultiselectDropdown
        :options="sectorsList"
        :selected-item="assignedSector"
        :searchable="false"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.SECTOR')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        @select="onClickAssignSector"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :searchable="false"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        @select="onClickAssignTeam"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="filteredAgentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>

<!-- <style scoped>
.ai-gradient-border {
  border: 1px solid transparent;
  background: linear-gradient(black, black) padding-box,
              linear-gradient(135deg, #2DE455, #BFFFCD, #FCFFCC, #133C1C) border-box;
}

.ai-gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  /* padding: 2px; */
  background: linear-gradient(135deg, #2DE455, #BFFFCD, #FCFFCC, #133C1C);
  border-radius: inherit;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: xor;
  -webkit-mask-composite: xor;
  pointer-events: none;
}
</style> -->

<style scoped>
.ai-gradient-border {
  position: relative;
  background: white;
  border-radius: 12px;
  z-index: 0;
  overflow: hidden; /* pra borda não vazar */
}

/* Tema escuro */
.dark .ai-gradient-border {
  background: #121212;
}

.ai-gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px; /* espessura da borda */
  border-radius: inherit;
  background: linear-gradient(
    135deg,
    #2de455,
    #bfffcd,
    #fcffcc,
    #133c1c,
    #2de455
  );
  background-size: 300% 300%; /* aumenta o tamanho pra permitir movimento */
  animation: moveGradient 4s linear infinite; /* animação contínua */
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
  z-index: -1;
}

.ai-gradient-border::after {
  content: '';
  position: absolute;
  inset: 2px;
  background: white;
  border-radius: calc(12px - 2px);
  z-index: -1;
}

/* Tema escuro - fundo interno preto */
.dark .ai-gradient-border::after {
  background: #121212;
}

/* animação do gradiente */
@keyframes moveGradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
